import React from "react";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { IconButton, Button, Link, Box, InputAdornment } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";

// Shared sx for the circular nav arrow buttons
const navArrowSx = {
  position: "absolute",
  top: "100px",
  transform: "translateY(100%)",
  zIndex: 10,
  backgroundColor: "#fff",
  boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
  width: 48,
  height: 48,
  "&:hover": { backgroundColor: "#f5f5f5" },
  "&.Mui-disabled": { backgroundColor: "#f0f0f0", opacity: 0.5 },
};

/**
 * Custom layout wrapper that adds:
 * - Close (X) button in header
 * - Footer with "Clear date" and "Select" button
 * Navigation arrows are handled by MUI's built-in slots (restyled)
 */
const CustomCalendarLayout = ({ onClose, onClear, onSelect, children }) => {
  return (
    <Box sx={{ position: "relative", overflow: "visible" }}>
      {/* Close button */}
      <IconButton
        onClick={() => { onClear(); onClose() }}
        sx={{
          position: "absolute",
          top: 20,
          right: 37,
          zIndex: 10,
          padding: "4px",
        }}
        size="small"
      >
        <CloseIcon sx={{ fontSize: 20, color: "#000" }} />
      </IconButton>

      {/* Calendar content */}
      {children}

      {/* Footer with Clear date and Select */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 24px 20px",
        }}
      >
        <Link
          component="button"
          underline="always"
          onClick={onClear}
          sx={{
            fontSize: "14px",
            color: "#1460AA",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Clear date
        </Link>
        <Button
          variant="contained"
          onClick={onSelect}
          sx={{
            backgroundColor: "#1460AA",
            borderRadius: "24px",
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 700,
            padding: "8px 32px",
            minWidth: 100,
            "&:hover": {
              backgroundColor: "#152f57",
            },
          }}
        >
          Select
        </Button>
      </Box>
    </Box>
  );
};

const DatePicker = ({
  value,
  onChange,
  open,
  onOpen,
  onClose,
  minDate,
  format = "MMM DD, YYYY",
  timezone = "system",
  errors = {},
  fieldName = "",
  startIcon,
  sx = {},
  ...rest
}) => {
  const [tempValue, setTempValue] = React.useState(value);

  React.useEffect(() => {
    
    value && setTempValue(value ? value : moment(''));
  }, [value]);

  const handleSelect = () => {
    onChange(tempValue);
    onClose();
  };

  const handleClear = () => {
    setTempValue(null);
    onChange(null);
  };

  return (
    <MuiDatePicker
      timezone={timezone}
      closeOnSelect={false}
      value={tempValue}
      onChange={(newValue) => setTempValue(newValue)}
      format={format}
      minDate={minDate}
      sx={{ width: "100%", "& input": { fontSize: "16px" }, ...sx }}
      slotProps={{
        textField: {
          fullWidth: true,
          variant: "standard",
          inputProps: {
            readOnly: true,
            onClick: onOpen,
          },
          InputProps: {
            startAdornment: startIcon ? (
              <InputAdornment position="start">{startIcon}</InputAdornment>
            ) : undefined,
          },
          error: Boolean(errors?.[fieldName]),
        },
        // Hide the default calendar icon at the end of the field.
        inputAdornment: {
          sx: { display: "none" },
        },
        previousIconButton: {
          sx: { ...navArrowSx, left: -24 },
        },
        nextIconButton: {
          sx: { ...navArrowSx, right: -24 },
        },
        desktopPaper: {
          sx: {
            overflow: "visible",
            borderRadius: "14px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",

            // Header
            "& .MuiPickersCalendarHeader-root": {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              padding: "16px 16px 8px",
              marginTop: 0,
            },
            "& .MuiPickersCalendarHeader-labelContainer": {
              fontSize: "18px",
              fontWeight: 700,
              color: "#000",
              margin: "0 auto",
              cursor: "default",
              pointerEvents: "none",
            },
            "& .MuiPickersCalendarHeader-switchViewButton": {
              display: "none",
            },
            // Hide default arrow switcher in header (arrows repositioned via slots)
            "& .MuiPickersArrowSwitcher-root": {
              position: "absolute",
              width: "100%",
              left: 0,
              top: 0,
              height: "100%",
              display: "flex",
              justifyContent: "space-between",
              pointerEvents: "none",
              "& .MuiIconButton-root": {
                pointerEvents: "auto",
              },
              "& .MuiPickersArrowSwitcher-spacer": {
                display: "none",
              },
            },
            // Day header styling (SUN, MON, etc.)
            "& .MuiDayCalendar-weekDayLabel": {
              fontSize: "11px",
              fontWeight: 600,
              color: "#666",
              textTransform: "uppercase",
              width: 40,
              height: 40,
            },
            // Day cell styling
            "& .MuiPickersDay-root": {
              fontSize: "14px",
              fontWeight: 400,
              width: 40,
              height: 40,
              color: "#333",
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.08)",
              },
            },
            // Selected day - blue circle
            "& .MuiPickersDay-root.Mui-selected": {
              backgroundColor: "#1460AA",
              color: "#fff",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#1460AA" },
              "&:focus": { backgroundColor: "#1460AA" },
            },
            // Today indicator
            "& .MuiPickersDay-today": {
              border: "none",
            },
            // Calendar container
            "& .MuiDateCalendar-root": {
              width: 360,
              maxHeight: "none",
              overflow: "visible",
            },
            "& .MuiDayCalendar-slideTransition": {
              minHeight: 260,
            },
            "& .MuiDayCalendar-weekContainer": {
              margin: "2px 0",
            },
            // Hide default action bar
            "& .MuiDialogActions-root": {
              display: "none",
            },
            "& .MuiPickersLayout-actionBar": {
              display: "none",
            },
          },
        },
      }}
      slots={{
        layout: (layoutProps) => (
          <CustomCalendarLayout
            onClose={onClose}
            onClear={handleClear}
            onSelect={handleSelect}
          >
            {layoutProps.children}
          </CustomCalendarLayout>
        ),
      }}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      {...rest}
    />
  );
};

export default DatePicker;
